import { useState, type Key } from 'react';
import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List, EditButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Input, Form, Row, Col, Image } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { PersonListOut } from '@/api/generated/schemas';
import { ModStatus } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { BulkModStatusControl } from '@/components/user_catalog/BulkModStatusControl';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { SelectFilter } from '@/components/common/SelectFilter';
import { NumberRangeFilter } from '@/components/common/NumberRangeFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const LectorList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, setFilters, filters, sorters } =
    useAdminTable<PersonListOut>({
      pagination: { pageSize: 20 },
      resource: 'user_catalog.lectors',
      syncWithLocation: true,
      onSearch,
      sorters: {
        initial: [{ field: 'updated_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('user_catalog.lectors.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {selectedRowKeys.length > 0 && (
        <Space style={{ marginBottom: 16 }}>
          <span>
            {t('user_catalog.common.bulk_mod_status.selected', {
              count: selectedRowKeys.length,
            })}
          </span>
          <BulkModStatusControl
            resource="user_catalog.lectors"
            ids={selectedRowKeys.map(Number)}
            onSuccess={() => setSelectedRowKeys([])}
          />
        </Space>
      )}

      <Table
        {...rowNav('user_catalog.lectors', tableProps)}
        rowKey="id"
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="cover_url"
          title={t('user_catalog.lectors.fields.cover_url')}
          render={(value: string) =>
            value ? (
              <Image
                src={value}
                height={60}
                style={{ objectFit: 'cover' }}
                preview={false}
              />
            ) : (
              '-'
            )
          }
        />

        <Table.Column
          dataIndex="name"
          title={t('user_catalog.lectors.fields.name')}
        />

        <Table.Column
          dataIndex="mod_status"
          title={t('base.fields.mod_status')}
          render={(value: string) => <ModStatusTag value={value} />}
          filtered={isFieldFiltered(filters as CrudFilter[], 'mod_status')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="mod_status"
              placeholder={t('base.fields.mod_status')}
              options={Object.values(ModStatus).map((value) => ({
                label: t(`base.mod_status.${value}`),
                value,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="search_popularity"
          title={t('user_catalog.common.fields.search_popularity')}
          sorter
          sortOrder={getSortOrder(sorters, 'search_popularity')}
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'search_popularity'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <NumberRangeFilter
              field="search_popularity"
              min={0}
              max={10}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(v: number) => v ?? 0}
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'updated_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="updated_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          render={(_, record: PersonListOut) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} />
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
